"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import {
	useParams,
	usePathname,
	useRouter,
	useSearchParams,
} from "next/navigation";
import { Alert, Button, Stack, Text } from "@leabankab/component-library";
import { PayloadPreview } from "@/components/form/payload-preview";
import { useStepPayloadPreviewContext } from "@/components/form/step-payload-preview-context";
import { BenefitList } from "@/components/ui";
import { TinkSymbol } from "@/components/ui/svg/tink-symbol";
import { useSubmitTask } from "@/hooks";
import { TaskType } from "@/types";
import {
	TINK_ERROR_FLAG,
	clearTinkErrorMessage,
	peekTinkErrorMessage,
} from "./tink-error-storage";
import { TinkConsentContext, TinkConsentPayload } from "./types";

interface Props {
	context: TinkConsentContext;
	taskId: string;
}

const SUBMIT_BODY = {
	tinkConsent: false,
} as TinkConsentPayload;

const resolveTinkLinkForCurrentOrigin = (tinkLink: string): string => {
	if (typeof window === "undefined") {
		return tinkLink;
	}

	const isLocalhost =
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1";

	if (!isLocalhost) {
		return tinkLink;
	}

	try {
		const url = new URL(tinkLink);
		url.searchParams.set(
			"redirect_uri",
			`${window.location.origin}/tink/callback`
		);
		return url.toString();
	} catch {
		return tinkLink;
	}
};

const TinkConsentPage = ({ context, taskId }: Props) => {
	const t = useTranslations("pages.TinkConsentPage");
	const flowPayload = useStepPayloadPreviewContext();
	const { flowId } = useParams<{ flowId: string }>();
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();

	const hasTinkErrorFlag = searchParams.get(TINK_ERROR_FLAG) === "1";
	const storedMessage = flowId ? peekTinkErrorMessage(flowId) : undefined;
	const errorMessage = hasTinkErrorFlag
		? (storedMessage ?? t("alert.tinkFailed"))
		: storedMessage;

	const submit = useSubmitTask<TinkConsentPayload>({
		taskId,
		processType: context.process,
		taskType: TaskType.TinkConsent,
	});

	const onSubmit = (body: TinkConsentPayload) => {
		if (flowId) {
			clearTinkErrorMessage(flowId);
		}
		return submit(body);
	};

	const handleRedirect = () => {
		if (flowId) {
			clearTinkErrorMessage(flowId);
		}
		window.location.href = resolveTinkLinkForCurrentOrigin(context.tinkLink);
	};

	// Side effect only: strip the one-shot query flag from the URL.
	useEffect(() => {
		if (!hasTinkErrorFlag) {
			return;
		}

		const nextParams = new URLSearchParams(searchParams.toString());
		nextParams.delete(TINK_ERROR_FLAG);
		const query = nextParams.toString();
		router.replace(query ? `${pathname}?${query}` : pathname);
	}, [hasTinkErrorFlag, pathname, router, searchParams]);

	return (
		<>
			<Text>
				{t(
					context.shouldBeOptional
						? "text.optionalTink.text"
						: "text.forcedTink.text"
				)}
			</Text>

			{context.shouldBeOptional && (
				<BenefitList
					items={[
						t("list.fastResponse"),
						t("list.accountDataReport"),
						t("list.automaticDataCollection"),
					]}
				/>
			)}

			<Stack gap={5}>
				{errorMessage && <Alert severity="error">{errorMessage}</Alert>}
				<Stack gap={1.5}>
					<Button
						onClick={handleRedirect}
						className="w-full gap-1 rounded-full"
					>
						<TinkSymbol width={32} height={24} />
						{t("button.connectViaTink")}
					</Button>
					{context.shouldBeOptional && (
						<Button
							onClick={() => onSubmit(SUBMIT_BODY)}
							variant="outline"
							className="w-full"
						>
							{t("button.manual")}
						</Button>
					)}
				</Stack>
			</Stack>
			<PayloadPreview flowPayload={flowPayload} payload={SUBMIT_BODY} />
		</>
	);
};

export default TinkConsentPage;
